import React, { useCallback, useState } from 'react';
import {
  IConnectionInput,
  IDataModelTestConnectionQuery,
  IDataModelTestConnectionQueryVariables,
} from '../../../../utils/apollo/graphqlSchema';
import {
  TestConnectionButton,
  TestConnectionForm,
  TestResults,
} from './DataModelerDataSourceInputUI';
import { useLazyQuery } from '@apollo/client';
import { DATA_MODEL_TEST_CONNECTION } from '../../../../utils/apollo/dataModeler/queries';

interface IProps {
  readOnly: boolean;
  connection: IConnectionInput;
}

const DataModelerTestConnection = ({ connection, readOnly }: IProps) => {
  const [lastTestErrors, setLastTestErrors] = useState<string[] | null>(null);
  const [testConnection, { loading }] = useLazyQuery<
    IDataModelTestConnectionQuery,
    IDataModelTestConnectionQueryVariables
  >(DATA_MODEL_TEST_CONNECTION);

  const isError = !loading && (lastTestErrors?.length ?? 0) > 0;
  const noResults = !loading && lastTestErrors === null;

  const mainMessage = loading
    ? 'Testing connection ...'
    : lastTestErrors === null
      ? 'No results'
      : lastTestErrors.length === 0
        ? 'Connection is good.'
        : 'Connection errors:';

  const handleTestConnectionClick = useCallback(async () => {
    if (readOnly) {
      return;
    }
    if (loading) {
      return;
    }
    testConnection({
      variables: {
        connection,
      },
    })
      .then((result) => {
        const testRes = result.data?.dataModelTestConnection;
        if (!testRes) {
          setLastTestErrors(['Unexpected error']);
          return;
        }
        const { success, errors } = testRes;
        if (success !== (errors.length === 0)) {
          setLastTestErrors(['Invalid response']);
        } else {
          setLastTestErrors(errors);
        }
      })
      .catch((reason) => {
        setLastTestErrors(['Unexpected error', reason]);
      });
  }, [testConnection, readOnly, loading, connection]);

  return (
    <TestConnectionForm>
      <TestConnectionButton
        type="button"
        size="default"
        onClick={handleTestConnectionClick}
        disabled={readOnly}
        loading={loading}
      >
        Test connection
      </TestConnectionButton>
      <TestResults $noResults={noResults} $isError={isError}>
        <div>{mainMessage}</div>
        {!loading &&
          lastTestErrors?.map((err, ix) => <div key={`err-${ix}`}>{err}</div>)}
      </TestResults>
    </TestConnectionForm>
  );
};

export default DataModelerTestConnection;
